import * as ToastPrimitive from '@radix-ui/react-toast';
import { AlertCircle, CheckCircle2, Info, X } from 'lucide-react';
import { createContext, ReactNode, useCallback, useContext, useState } from 'react';
import { cn } from '../../lib/cn';

/**
 * Minimal in-app toast system. Intentionally NOT used for routine actions
 * (login, logout, navigation, refresh, save) — those should feel
 * silent. Reserved for:
 *   - genuine errors the user couldn't otherwise see (background sync
 *     failed, OCR job died)
 *   - explicit user-initiated mutations whose success isn't obvious from
 *     the page state (e.g. "Invite sent")
 *
 * If you find yourself reaching for toast on every successful save,
 * stop — render the new state in the page instead.
 */

type ToastKind = 'error' | 'info' | 'success';

interface ToastAction {
  label: string;
  onClick: () => void;
}

interface ToastOptions {
  description?: string;
  /** Optional inline action button (e.g. "Undo" on an AI-action receipt). */
  action?: ToastAction;
}

interface ToastPayload {
  id: number;
  kind: ToastKind;
  message: string;
  description?: string;
  action?: ToastAction;
}

interface ToastAPI {
  error: (message: string, description?: string) => void;
  info: (message: string, description?: string) => void;
  success: (message: string, description?: string) => void;
  /** Toast with an inline action button. Stays a touch longer so the
   *  action (e.g. Undo) is actually reachable. */
  withAction: (kind: ToastKind, message: string, action: ToastAction, description?: string) => void;
}

const ToastCtx = createContext<ToastAPI | null>(null);

let _seq = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastPayload[]>([]);

  const remove = useCallback((id: number) => {
    setItems((arr) => arr.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (kind: ToastKind, message: string, opts?: ToastOptions) => {
      const id = ++_seq;
      setItems((arr) => [
        ...arr,
        { id, kind, message, description: opts?.description, action: opts?.action },
      ]);
    },
    [],
  );

  const api: ToastAPI = {
    error: (m, d) => push('error', m, { description: d }),
    info: (m, d) => push('info', m, { description: d }),
    success: (m, d) => push('success', m, { description: d }),
    withAction: (kind, m, action, d) => push(kind, m, { description: d, action }),
  };

  return (
    <ToastCtx.Provider value={api}>
      <ToastPrimitive.Provider swipeDirection="right" duration={5_000}>
        {children}
        {items.map((t) => (
          <ToastPrimitive.Root
            key={t.id}
            open
            onOpenChange={(o) => { if (!o) remove(t.id); }}
            className={cn(
              'rounded-lg border bg-white dark:bg-ink-900 px-4 py-3 shadow-lg flex items-start gap-3 w-80',
              'data-[state=open]:animate-in data-[state=open]:slide-in-from-right-full',
              'data-[state=closed]:animate-out data-[state=closed]:fade-out-80',
              t.kind === 'error'
                ? 'border-warn-200 dark:border-warn-700'
                : t.kind === 'success'
                ? 'border-emerald-200 dark:border-emerald-700'
                : 'border-ink-200 dark:border-ink-800',
            )}
          >
            <Icon kind={t.kind} />
            <div className="flex-1 min-w-0">
              <ToastPrimitive.Title className="text-sm font-medium text-ink-900 dark:text-ink-100">
                {t.message}
              </ToastPrimitive.Title>
              {t.description ? (
                <ToastPrimitive.Description className="mt-0.5 text-xs text-ink-600 dark:text-ink-300 leading-relaxed">
                  {t.description}
                </ToastPrimitive.Description>
              ) : null}
              {t.action ? (
                <ToastPrimitive.Action
                  altText={t.action.label}
                  asChild
                  onClick={() => {
                    t.action!.onClick();
                    remove(t.id);
                  }}
                >
                  <button
                    type="button"
                    className="mt-2 inline-flex items-center rounded-md bg-accent-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-accent-700"
                  >
                    {t.action.label}
                  </button>
                </ToastPrimitive.Action>
              ) : null}
            </div>
            <ToastPrimitive.Close className="text-ink-400 hover:text-ink-700 dark:hover:text-ink-200" aria-label="Dismiss">
              <X size={14} />
            </ToastPrimitive.Close>
          </ToastPrimitive.Root>
        ))}
        <ToastPrimitive.Viewport className="fixed right-4 bottom-4 z-[60] flex flex-col gap-2 outline-none" />
      </ToastPrimitive.Provider>
    </ToastCtx.Provider>
  );
}

function Icon({ kind }: { kind: ToastKind }) {
  if (kind === 'error') return <AlertCircle size={16} className="text-warn-600 mt-0.5" />;
  if (kind === 'success') return <CheckCircle2 size={16} className="text-emerald-600 mt-0.5" />;
  return <Info size={16} className="text-ink-500 mt-0.5" />;
}

export function useToast() {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error('useToast() must be used inside <ToastProvider>');
  return ctx;
}
